import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Search, ShoppingCart, ArrowLeft, Home, Plus, Package, Loader2, Mail, Scissors, Stethoscope
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import ProductDetailDialog from "@/components/ProductDetailDialog";
import CatalogNavDropdown from "@/components/CatalogNavDropdown";
import MarketplaceSearchBar from "@/components/MarketplaceSearchBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * BEAR-ENT images are served off cdn.shopify.com — route them through our
 * existing Shopify image proxy (originally built for Sol-M) to bypass CDN
 * hotlink protection.
 */
function proxyImg(url) {
  if (!url) return null;
  if (url.includes("cdn.shopify.com")) {
    return `${BACKEND}/api/solm/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

const DETAIL_CACHE = {};
async function fetchBearEntProduct(id) {
  if (DETAIL_CACHE[id]) return DETAIL_CACHE[id];
  const r = await fetch(`${API}/bear-ent/products/${id}`);
  if (!r.ok) throw new Error();
  const data = await r.json();
  DETAIL_CACHE[id] = data;
  return data;
}

/**
 * ENT marketplace page (Ear / Nose / Throat — BEAR-ENT partner).
 * Route: /sterilemate/ent-:slug — e.g. /sterilemate/ent-ear
 *
 * Structurally matches SolMCategoryPage so users get the same grid,
 * filters, quote-cart flow, and deep-link support across every partner.
 */
export default function BearEntCategoryPage() {
  const location = useLocation();
  // URL is /sterilemate/ent-<slug> → slug is the tail after "ent-"
  const tail = location.pathname.split("/").pop();
  const slug = tail?.startsWith("ent-") ? tail.slice(4) : tail;
  const { cart, setShowCart, addItem } = useQuoteCart();

  const [category, setCategory] = useState(null);
  const [partner, setPartner] = useState("BEAR-ENT");
  const [partnerBlurb, setPartnerBlurb] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [detailProduct, setDetailProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subTagFilter, setSubTagFilter] = useState(
    () => new URLSearchParams(window.location.search).get("type") || ""
  );

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const [catRes, allRes] = await Promise.all([
          fetch(`${API}/bear-ent/category/${slug}`),
          fetch(`${API}/bear-ent/catalog`),
        ]);
        if (!catRes.ok) throw new Error("Category not found");
        const cd = await catRes.json();
        const ad = await allRes.json();
        setCategory(cd.category);
        setPartner(cd.partner || "BEAR-ENT");
        setPartnerBlurb(cd.partner_description || "");
        setAllCategories(ad.categories || []);
      } catch {
        toast.error("Failed to load ENT category");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const openDetail = useCallback(async (p) => {
    setDetailLoading(true);
    setDetailProduct({ ...p, _loading: true });
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("product", p.id);
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
    try {
      const { product, category } = await fetchBearEntProduct(p.id);
      setDetailProduct({ ...product, category });
    } catch {
      toast.error("Could not load product");
      setDetailProduct(null);
    } finally { setDetailLoading(false); }
  }, []);

  const closeDetail = () => {
    setDetailProduct(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("product");
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
  };

  // Deep link: open modal when URL has ?product=<id>
  useEffect(() => {
    if (!category) return;
    const pid = new URLSearchParams(window.location.search).get("product");
    if (pid && !detailProduct) {
      const p = category.products.find(x => x.id === pid || x.erp === pid);
      if (p) openDetail(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const filtered = useMemo(() => {
    if (!category) return [];
    const normalize = (p) => ({
      ...p,
      image: p.image
        || (p.images && p.images[0] && (p.images[0].medium || p.images[0].large))
        || null,
      variant_count: p.variant_count ?? (p.variants ? p.variants.length : 0),
    });
    const q = search.trim().toLowerCase();
    const sub = subTagFilter.toLowerCase();
    const base = category.products.map(normalize);
    let out = base;
    if (sub) {
      out = out.filter(p => (p.sub_tags || []).some(t => t.toLowerCase() === sub));
    }
    if (q) {
      out = out.filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.erp || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q));
    }
    return out;
  }, [category, search, subTagFilter]);

  // Collect unique sub-tags inside this category for filter chips
  const subTags = useMemo(() => {
    if (!category) return [];
    const tags = new Set();
    for (const p of category.products) {
      for (const t of (p.sub_tags || [])) tags.add(t);
    }
    return Array.from(tags).sort();
  }, [category]);

  // Reset sub-tag filter + read ?type= when slug changes (deep-link support)
  useEffect(() => {
    const urlType = new URLSearchParams(window.location.search).get("type") || "";
    setSubTagFilter(urlType);
  }, [slug]);

  // Sync subTagFilter → URL ?type= so deep-links are shareable
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (subTagFilter) url.searchParams.set("type", subTagFilter);
      else url.searchParams.delete("type");
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
  }, [subTagFilter]);

  const shareUrlBuilder = (p) =>
    `${window.location.origin}/sterilemate/ent-${slug}?product=${encodeURIComponent(p.id || p.erp)}`;

  const shareViaEmailCard = (p) => {
    const url = shareUrlBuilder(p);
    const subject = `Sterile Mate — ${p.name}${p.erp ? ` (SKU: ${p.erp})` : ""}`;
    const body = [
      `Take a look at this ENT product from the Sterile Mate Marketplace:`,
      "",
      p.name,
      p.erp ? `SKU: ${p.erp}` : "",
      p.brand ? `Brand: ${p.brand}` : "",
      "",
      p.description || "",
      "",
      `View full details & request a quote:`,
      url,
      "",
      `— Sterile Mate · 281.456.3458 · orders@sterilemate.com`,
    ].filter(Boolean).join("\n");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading ENT catalog...
      </div>
    );
  }
  if (!category) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-slate-600">ENT category not found.</p>
          <Link to="/sterilemate/surgical" className="text-blue-700 underline mt-3 inline-block">Back to Surgical</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`bearent-cat-${slug}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1a0e2e] via-[#2d1b4a] to-[#1a0e2e] border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 transition-colors"
                data-testid="bearent-back-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 transition-colors"
                data-testid="bearent-home-btn">
                <Home className="w-3.5 h-3.5" /> Home
              </Link>
              <Link
                to="/sterilemate/surgical"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-100 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/40 rounded-lg px-3 py-1.5 transition-colors"
                data-testid="back-to-surgical">
                <Scissors className="w-3.5 h-3.5" /> Surgical Marketplace
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-semibold text-purple-300 uppercase tracking-[0.2em] mb-1 flex items-center gap-2">
                <Stethoscope className="w-3.5 h-3.5" /> ENT Partner · {partner}
              </p>
              <h1 className="text-3xl sm:text-4xl font-black text-white">{category.name}</h1>
              <p className="text-purple-200 mt-1">{category.products.length} products</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href={`${API}/downloads/ent-catalog`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-xl px-5 py-3 flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-colors"
                data-testid="download-ent-catalog-pdf-btn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>Download Catalog PDF</span>
              </a>
              <button
                onClick={() => setShowCart(true)}
                className="relative bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-5 py-3 text-white flex items-center gap-2"
                data-testid="open-cart-btn">
                <ShoppingCart className="w-5 h-5" />
                <span className="font-semibold">Quote Cart</span>
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>
              <CatalogNavDropdown />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Partner banner */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8" data-testid="partner-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center shrink-0">
              <Stethoscope className="w-6 h-6 text-purple-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900 mb-1">
                Sterile Mate × {partner} — German Stainless ENT Instruments
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed">{partnerBlurb}</p>
              <p className="text-sm text-slate-500 mt-2">{category.description}</p>
              <a
                href={`${API}/downloads/ent-catalog`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-amber-700 hover:text-amber-800 underline-offset-4 hover:underline"
                data-testid="download-ent-catalog-banner-link">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                Download full Sterile Mate ENT Catalog (PDF · 2.6 MB · 29 pages)
              </a>
            </div>
          </div>
        </div>

        {/* Other ENT categories + Surgical link */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Browse ENT:</span>
          {allCategories.map(c => (
            <Link
              key={c.slug}
              to={`/sterilemate/ent-${c.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                c.slug === slug
                  ? "bg-purple-50 border-purple-500 text-purple-800"
                  : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
              }`}
              data-testid={`bearent-nav-${c.slug}`}>
              {c.name} <span className="text-slate-400">({c.products.length})</span>
            </Link>
          ))}
          <Link
            to="/sterilemate/surgical"
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100"
            data-testid="nav-surgical">
            <Scissors className="inline w-3 h-3 mr-1" /> Surgical
          </Link>
        </div>

        {/* Unified search */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 max-w-2xl">
            <MarketplaceSearchBar placeholder="Search all products by name, SKU, or item number..." />
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter this page..."
              className="pl-11 bg-white border-slate-300 text-slate-900 h-10"
              data-testid="bearent-filter" />
          </div>
        </div>

        {/* Sub-tag filter chips (Curettes, Scissors, Forceps, etc.) */}
        {subTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="bearent-subtag-bar">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider mr-1">Instrument type:</span>
            <button
              onClick={() => setSubTagFilter("")}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                !subTagFilter
                  ? "bg-purple-50 border-purple-500 text-purple-800"
                  : "bg-white border-slate-300 text-slate-600 hover:border-slate-500"
              }`}
              data-testid="subtag-all">
              All ({category.products.length})
            </button>
            {subTags.map(tag => {
              const count = category.products.filter(p => (p.sub_tags || []).includes(tag)).length;
              return (
                <button
                  key={tag}
                  onClick={() => setSubTagFilter(tag)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    subTagFilter === tag
                      ? "bg-purple-50 border-purple-500 text-purple-800"
                      : "bg-white border-slate-300 text-slate-600 hover:border-slate-500"
                  }`}
                  data-testid={`subtag-${tag.replace(/\s+/g, '-').toLowerCase()}`}>
                  {tag} <span className="text-slate-400">({count})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Product grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-slate-700 font-medium">No matches</h3>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="bearent-products-grid">
            {filtered.map((p) => (
              <div key={p.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-purple-400 hover:shadow-md transition-all cursor-pointer group relative"
                data-testid={`product-${p.erp}`}
                onClick={() => openDetail(p)}>
                <button
                  onClick={(e) => { e.stopPropagation(); shareViaEmailCard(p); }}
                  className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/95 hover:bg-purple-600 border border-slate-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  title="Email this product"
                  data-testid={`card-share-${p.erp}`}>
                  <Mail className="w-3.5 h-3.5 text-slate-700 hover:text-white" />
                </button>
                <div className="bg-slate-50 p-3 flex items-center justify-center h-32">
                  {p.image
                    ? <img src={proxyImg(p.image)} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                    : <Package className="w-10 h-10 text-slate-300" />}
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-slate-500 font-mono truncate">{p.erp}</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 leading-tight line-clamp-2">{p.name}</p>
                  {p.brand && <p className="text-xs text-purple-700 mt-0.5">{p.brand}</p>}
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</p>
                  {p.variant_count > 0 && (
                    <p className="text-[10px] text-amber-700 mt-1 font-semibold">
                      {p.variant_count} sizes/options
                    </p>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addItem({ erp: p.erp, name: p.name, image: p.image, source: partner });
                    }}
                    className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
                    data-testid={`add-${p.erp}`}>
                    <Plus className="w-3.5 h-3.5" /> Add to Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#201238] to-[#2d1b4a] rounded-xl p-8 border border-purple-500/20 text-center mt-10">
          <h3 className="text-2xl font-bold text-white mb-2">Need Pricing?</h3>
          <p className="text-purple-200 mb-6">Add products to your Quote Cart and submit — we respond within 2 hours.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button onClick={() => setShowCart(true)} className="bg-amber-500 hover:bg-amber-600 text-white">
              <ShoppingCart className="w-4 h-4 mr-2" /> Open Quote Cart ({cart.length})
            </Button>
            <Link to="/sterilemate/surgical">
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10">
                <Scissors className="w-4 h-4 mr-2" /> Back to Surgical Marketplace
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <ProductDetailDialog
        product={detailProduct ? {
          ...detailProduct,
          images: (detailProduct.images || []).map(i => ({
            ...i,
            small: proxyImg(i.small), medium: proxyImg(i.medium), large: proxyImg(i.large),
          })),
          variants: (detailProduct.variants || []).map(v => ({ ...v, image: proxyImg(v.image) })),
        } : null}
        loading={detailLoading}
        onClose={closeDetail}
        source={partner}
        shareUrlBuilder={shareUrlBuilder}
      />
    </div>
  );
}
